/**
 * A live "in 45s" countdown for queue rows that are about to go out.
 *
 * relativeTime() rounds to whole minutes, so anything inside the last minute
 * read "just now" and then sat there — the most interesting moment in the queue
 * was the one moment the UI stopped moving.
 *
 * The timer is adaptive on purpose: one tick per SECOND only inside the final
 * minute, and otherwise a single wake-up on the next minute boundary. A row two
 * hours out re-renders about once an hour, not 3600 times, so a queue of a
 * hundred rows costs a hundred sleeping timers rather than a hundred per second.
 */
import React from 'react'
import { relativeTime } from './format'

/** How the label reads for a given moment. Pure, so it is trivially testable. */
export function countdownLabel(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return 'next tick'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const diff = then - now
  // Due, or overdue: the sender claims it on the next tick.
  if (diff <= 0) return 'sending…'
  // Under a minute — count it down by the second.
  if (diff < 60_000) return `in ${Math.max(1, Math.ceil(diff / 1000))}s`
  // Over a minute, the existing wording stands: "in 5m", "in 2h", "in 3d".
  return relativeTime(iso)
}

/** ms until the label would next change. null once it can never change again. */
function nextTickIn(then: number, now: number): number | null {
  const diff = then - now
  if (diff <= 0) return null // "sending…" is terminal for this hook
  if (diff <= 60_000) return Math.min(1000, diff) // per second, then land on due
  // Otherwise wake when the minute reading rolls over.
  return Math.min(60_000, diff - 60_000 + 1000)
}

/**
 * Re-renders the caller only when the label would actually change.
 *
 * `active` is false for a paused row — it reads "held" and has no countdown, so
 * it must not hold a timer either.
 */
export function useCountdown(iso: string | null | undefined, active = true): string {
  const [, tick] = React.useReducer((n: number) => n + 1, 0)

  React.useEffect(() => {
    if (!iso || !active) return
    const then = new Date(iso).getTime()
    if (Number.isNaN(then)) return

    let timer: number | undefined
    const schedule = () => {
      const delay = nextTickIn(then, Date.now())
      if (delay === null) return // due — nothing left to count
      timer = window.setTimeout(() => {
        tick()
        schedule()
      }, delay)
    }
    schedule()

    // Unmount, or a row whose time changed, must not leave a timer behind.
    return () => {
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [iso, active])

  return countdownLabel(iso)
}
